import React, { useEffect, useState } from "react";
import { makerCheckerActions } from "../../utils/constants";
import { Modal, ModalBody, ModalHeader, ModalTitle } from "react-bootstrap";
import { buildComparisonGroups } from "./payload/buildComparisonGroups";
import ComparisonTable from "./payload/ComparisonTable";
import LegacyBrandDetails from "./payload/LegacyBrandDetails";
import { brandFieldLabel, formatBrandValue, omitBrandFields } from "./payload/brandFormat";
import { isKeyedPayload, resolveDisplayName } from "./payload/constants";

// maker_checker_logs.rej_reason is VARCHAR(100).
const REJ_REASON_MAX = 100;


const BrandChecker = ({ data, onApprove, onReject , navigation,isfromMail,onClose,isOnlyView=false}) => {
  const [reason, setReason] = useState("");
  const [showDrawer, setShowDrawer] = useState(false);

  const handleConfirmReject = () => {
    if (!reason.trim()) {
      alert("Please provide a reason before rejecting.");
      return;
    }
    onReject(reason);
    setReason("");
    setShowDrawer(false);
  };
  const handleRejectClick = () => {
    setShowDrawer(true);
  };
  function handleRequestClose(event) {
    if(isfromMail)
    {
      localStorage.removeItem('mc_id');
      navigation()
    }
    else
    onClose()
  }
  return (
    <Modal
      show={true}
      onHide={handleRequestClose}
      /* xl (not lg): the body is now a 3-column Field/Before/After table beside the logo,
         which does not fit lg's ~800px without scrolling sideways. */
      size="xl"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          BrandChecker :{" "}
          {makerCheckerActions.find((a) => a.value === data?.action_type)?.label || data?.action_type}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Was `max-w-2xl h-[500px]`: a 672px cap (inside a wider modal) minus the 144px
            logo left the comparison table ~500px, forcing a sideways scroll, and the fixed
            height forced a vertical one regardless of content. Now it uses the full modal
            width and grows with content up to a viewport-relative cap. min-h keeps a floor
            for the absolutely-positioned reject drawer below. */}
        <div className="w-full min-h-[320px] max-h-[75vh] border border-gray-300 bg-white rounded-md relative flex flex-col">
          <div className="flex-1 overflow-y-auto p-4 sm:p-5">
            {(() => {
              /*
               * v1 rows go through the same dispatcher + ComparisonTable as every other
               * maker-checker screen; the brand-specific labelling (Sponsored / Active,
               * brand_category_label) rides along as formatValue/formatField hooks rather
               * than a forked renderer.
               *
               * v0 rows keep the flat dump: legacy birdview stored the submitted body in
               * `payload`, so a before/after diff would render an add backwards.
               *
               * resolveDisplayName reads request_payload first, so the logo/name resolve for
               * both models. (Previously this read `data.payload` directly for adds — which
               * is `{}` under the keyed model, and `{}` is truthy, so the "No details"
               * guard did not fire and the panel rendered EMPTY.)
               */
              const logoUrl = resolveDisplayName(data, "logo_url");
              const brandName = resolveDisplayName(data, "brand_name");

              /* Fixed 9rem column on md+ (shrink-0 so it never steals width from the table);
                 full-width block above the fields on mobile. The raw URL is long enough to
                 blow out the column, so it is a short "View full image" link instead. */
              const LogoPanel = () =>
                logoUrl ? (
                  <div className="w-full md:w-36 md:shrink-0 order-first md:order-none">
                    <div className="w-full h-36 md:w-36 border border-gray-200 rounded-md p-2 bg-white flex items-center justify-center">
                      <img
                        src={logoUrl}
                        alt={brandName ? `${brandName} logo` : "Brand logo"}
                        className="max-w-full max-h-full object-contain"
                        referrerPolicy="no-referrer"
                      />
                    </div>
                    <a
                      href={logoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1.5 block text-xs text-blue-600 text-center underline"
                    >
                      View full image
                    </a>
                  </div>
                ) : null;

              // ---- v0 branch: delete with LegacyBrandDetails once no legacy rows remain ----
              if (!isKeyedPayload(data)) {
                const legacyPayload = data?.action_type === 2 ? data?.updated_payload : data?.payload;
                if (!legacyPayload || !Object.keys(legacyPayload).length) {
                  return <p className="text-gray-500 italic">No details available</p>;
                }
                return (
                  <div className="flex flex-col md:flex-row items-start gap-4 md:gap-6">
                    <div className="flex-1 min-w-0 w-full">
                      <LegacyBrandDetails payload={legacyPayload} />
                    </div>
                    <LogoPanel />
                  </div>
                );
              }
              // -----------------------------------------------------------------------------

              const groups = omitBrandFields(buildComparisonGroups(data));
              return (
                /* min-w-0 is load-bearing: without it the flex item refuses to shrink below
                   its table's intrinsic width and pushes the logo off-screen. */
                <div className="flex flex-col md:flex-row items-start gap-4 md:gap-6">
                  <div className="flex-1 min-w-0 w-full">
                    <ComparisonTable
                      groups={groups}
                      emptyLabel="No details available"
                      formatValue={formatBrandValue}
                      formatField={brandFieldLabel}
                    />
                  </div>
                  <LogoPanel />
                </div>
              );
            })()}
          </div>

          {/* Action Buttons */}
          {!isOnlyView && (
            <div className="flex justify-between border-t border-gray-300 p-4 bg-white">
              <button
                className="bg-red-500 text-white px-5 py-2 rounded-md"
                onClick={handleRejectClick}
              >
                Reject
              </button>
              <button
                className="bg-blue-500 text-white px-5 py-2 rounded-md"
                onClick={onApprove}
              >
                Approve
              </button>
            </div>
          )}

          {/* Drawer */}
          {showDrawer && (
            <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-300 p-4 shadow-lg">
              <textarea
                autoFocus
                maxLength={REJ_REASON_MAX}
                className="w-full h-20 p-2 border border-gray-300 rounded-md resize-none"
                placeholder="Enter reason for rejection..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Escape") { setShowDrawer(false); setReason(""); } }}
              />
              {/* rej_reason is VARCHAR(100) — cap it here so it is never truncated server-side. */}
              <div className="mt-1 text-xs text-gray-400">{reason.length}/{REJ_REASON_MAX}</div>
              <div className="flex justify-end gap-2 mt-2">
                <button
                  className="px-3 py-1 border border-gray-300 rounded-md bg-gray-100"
                  onClick={() => { setShowDrawer(false); setReason(""); }}
                >
                  Cancel
                </button>
                <button
                  disabled={!reason.trim()}
                  className="px-3 py-1 rounded-md bg-red-500 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleConfirmReject}
                >
                  Confirm Reject
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default BrandChecker;
